import type { TranslationKey } from "@/config/translationKeys";
import { getLinksCell, splitLinksField } from "./links";
import { getObjectiveRawValue } from "./objectives";
import {
  getAllFields,
  hasAnyAnswer,
  normalizeAnswers,
  type Answers,
  type QuestionnaireDefinition,
} from "./questionnaire";
import { toText } from "./text";
import type { Intervention, SourceRow, View } from "./types";
import { WORKING_COMMENT_COLUMN } from "./workingComments";

/** Editable public fields of an intervention. */
export interface PublicDraft {
  titleNo: string;
  titleEn: string;
  descriptionNo: string;
  descriptionEn: string;
  youngest: string;
  oldest: string;
  links: string;
  objectives: string;
  arena: string;
  region: string;
  /** Classification code per source column. */
  classifications: Record<string, string>;
}

/** One select per source column; e.g. the objectives view reuses EvidenceLevel and gets none. */
/**
 * One classification field per source column, for the columns used by at least
 * one visible view. Fields of hidden views are left out of the form; their stored
 * values are kept, because a saved row starts from the existing one.
 */
export function getClassificationViews(views: View[]): View[] {
  const seen = new Set<string>();
  return views.filter(
    (view) => view.visible && !seen.has(view.column) && (seen.add(view.column), true),
  );
}

export function createPublicDraft(
  item: Intervention | null,
  views: View[],
  defaultRegion: string,
): PublicDraft {
  const source: SourceRow = item?.sourceRow ?? {};
  const linksCell = getLinksCell(source);
  const pick = (key: string, fallback: string) =>
    source[key] != null ? String(source[key]) : fallback;
  return {
    titleNo: pick("Title_NO", item?.display.no ?? ""),
    titleEn: pick("Title_EN", item?.display.en ?? ""),
    descriptionNo: pick("Description_NO", item?.description.no ?? ""),
    descriptionEn: pick("Description_EN", item?.description.en ?? ""),
    youngest: item?.youngest != null ? String(item.youngest) : "",
    oldest: item?.oldest != null ? String(item.oldest) : "",
    links: toText(linksCell) ? splitLinksField(linksCell).join(";") : (item?.links ?? []).join(";"),
    objectives: getObjectiveRawValue(source),
    arena: item?.arena ?? "",
    region: item ? item.region : defaultRegion,
    classifications: Object.fromEntries(
      getClassificationViews(views).map((view) => [view.column, item?.codes[view.id] ?? ""]),
    ),
  };
}

export interface SaveContext {
  isCreate: boolean;
  isRespondent: boolean;
  /** Superadmins and local admins edit classifications. */
  includeClassifications: boolean;
  /** Respondent action; `null` for administrators. */
  requestedStatus: "draft" | "submitted" | null;
  /** Only validate Visningsinformasjon when saving that tab. */
  saveSection?: "display" | "questionnaire";
  registeredArenas: string[];
  item: Intervention | null;
  config: QuestionnaireDefinition | null;
  /** Saved private answers (kept for unknown keys). */
  existingAnswers: Answers;
  hasExistingResponse: boolean;
  views: View[];
}

export type SaveValidation =
  | { ok: false; error: TranslationKey }
  | {
      ok: true;
      rowData: SourceRow;
      answers: Answers | null;
      config: QuestionnaireDefinition;
    };

/**
 * Validates the editor state and builds the payload for
 * `save_intervention_questionnaire`. Mirrors the rules enforced by the RPC.
 */
export function validateInterventionDraft(
  draft: PublicDraft,
  answersDraft: Answers,
  context: SaveContext,
): SaveValidation {
  const { isCreate, isRespondent, config } = context;
  const isDisplaySave = !isRespondent && context.saveSection !== "questionnaire";
  const arena = draft.arena.trim();
  // Respondents do not choose an arena; an administrator sets it before publishing.
  // An empty Arena is allowed during data entry; it is required when an
  // administrator publishes the intervention (database-enforced).
  if (isDisplaySave && arena && !context.registeredArenas.includes(arena))
    return { ok: false, error: "formArenaRequired" };
  const region = draft.region.trim();
  if (!region || !config) return { ok: false, error: "formChooseLocation" };

  const answers = normalizeAnswers(config, context.existingAnswers, answersDraft);
  const answerTitle = (key: "Title_NO" | "Title_EN") =>
    typeof answers[key] === "string" ? (answers[key] as string).trim() : "";
  let titleNo = draft.titleNo.trim();
  let titleEn = draft.titleEn.trim();
  if (isDisplaySave && isCreate) {
    titleNo ||= answerTitle("Title_NO");
    titleEn ||= answerTitle("Title_EN");
  }
  if (isDisplaySave && !titleNo && !titleEn) return { ok: false, error: "editTitleRequired" };
  // Validate the public age range only when saving Visningsinformasjon.
  // Questionnaire saves must work for respondents and admins even when these
  // unrelated display fields are incomplete or not visible.
  if (isDisplaySave) {
    const youngest = draft.youngest.trim();
    const oldest = draft.oldest.trim();
    const isAge = (value: string) => /^\d+$/.test(value);
    if (!isAge(youngest) || !isAge(oldest) || Number(youngest) > Number(oldest))
      return { ok: false, error: "formAgeRequired" };
  }
  if (
    isRespondent &&
    context.requestedStatus === "submitted" &&
    !answerTitle("Title_NO") &&
    !answerTitle("Title_EN")
  ) {
    return { ok: false, error: "editTitleRequired" };
  }

  const invalidNumber = getAllFields(config)
    .filter((field) => field.type === "number")
    .some((field) => {
      const value = answers[field.key];
      return (
        value !== null &&
        value !== undefined &&
        (!Number.isSafeInteger(value) || (value as number) < 0)
      );
    });
  const { Youngest: youngest, Oldest: oldest } = answers;
  const reversedRange =
    answers.II_4 === "range" &&
    typeof youngest === "number" &&
    typeof oldest === "number" &&
    youngest > oldest;
  if (invalidNumber || reversedRange) return { ok: false, error: "formInvalidNumber" };

  const rowData: SourceRow = {
    ...(isCreate ? {} : context.item?.sourceRow),
    Title_NO: titleNo,
    Title_EN: titleEn,
    Description_NO: draft.descriptionNo.trim(),
    Description_EN: draft.descriptionEn.trim(),
    Arena: arena,
    Region: region,
    Youngest: draft.youngest.trim(),
    Oldest: draft.oldest.trim(),
    links: splitLinksField(draft.links).join(";"),
    Objectives: draft.objectives.trim(),
  };
  if (!isCreate && context.item) rowData.ID = context.item.itemNumber;
  if (context.includeClassifications) {
    for (const view of getClassificationViews(context.views))
      rowData[view.column] = draft.classifications[view.column] ?? "";
  }
  delete rowData.unodc;
  // Working comments live in their own table; never keep them in public row data.
  delete rowData[WORKING_COMMENT_COLUMN];

  const keepAnswers = isRespondent || context.hasExistingResponse || hasAnyAnswer(answers);
  return { ok: true, rowData, answers: keepAnswers ? answers : null, config };
}
